import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { getCurrentBearer, isAuthEnabled, refreshBearer, startLogin } from './userManager';

interface RequireAuthProps {
  children: ReactNode;
}

/**
 * Gates protected routes. When Cognito is not configured (mock/dev mode) it is
 * a transparent pass-through. Otherwise it checks for a live session and, if
 * absent, redirects to the Hosted UI while showing a spinner.
 */
export const RequireAuth = ({ children }: RequireAuthProps) => {
  // Auth disabled → render immediately, no session check, no flash.
  const [status, setStatus] = useState<'checking' | 'authed'>(
    isAuthEnabled() ? 'checking' : 'authed',
  );
  const redirecting = useRef(false);

  useEffect(() => {
    if (!isAuthEnabled()) return;
    let active = true;
    const redirectToLogin = () => {
      if (!redirecting.current) {
        redirecting.current = true;
        void startLogin();
      }
    };

    void getCurrentBearer()
      .then((token) => {
        if (token) return token;
        // Access token missing/expired: try a silent refresh via the refresh
        // token (valid up to 30 days) before forcing a Hosted UI redirect.
        return refreshBearer();
      })
      .then((token) => {
        if (!active) return;
        if (token) {
          setStatus('authed');
        } else {
          redirectToLogin();
        }
      })
      .catch(() => {
        // Corrupt storage / quota errors: treat as no session rather than
        // leaving the spinner stuck forever.
        if (!active) return;
        redirectToLogin();
      });
    return () => {
      active = false;
    };
  }, []);

  if (status === 'authed') return <>{children}</>;

  return (
    <Box component="main" sx={{ flex: 1, display: 'grid', placeItems: 'center' }}>
      <Stack spacing={2} alignItems="center">
        <CircularProgress aria-label="Checking your session" />
        <Typography color="text.secondary">Redirecting to sign-in…</Typography>
      </Stack>
    </Box>
  );
};
